<template>
	<div class="idv" v-if="isNoAccess">
		<!-- Нет доступа показываем только при явном отказе сервера. -->
		<ErrorNoAccess />
	</div>

	<div class="idv" v-else-if="isLoadError">
		<ErrorInstanceLoad />
	</div>

	<div class="idv" v-else-if="hasInstanceAccess">
		<!-- //////////////// -->
		<!-- INSTANCE DETAIL INFO -->
		<div class="idv__info">
			<InstanceDetailInfo :paid-options="safePaidOptions" />
		</div>

		<!-- //////////////// -->
		<!-- INSTANCE OPTIONS -->
		<div class="idv__options">
			<!-- Этот блок показываем, если число купленных опций меньше чем число доступных для данного тарифа -->
			<div
				v-if="
					safePaidOptions.length < safeOptionList.length &&
					safeInstance.status !== STATUS.DELETE
				"
			>
				<!-- TODO - На тот счлучай, если нужно будет вернуть старый функционал -->
				<!-- <InstanceDetailOptions :paid-options="payOptionsLnkInstance" /> -->

				<!-- Redesign of the InstanceDetailOptions component -->
				<InstanceDetailOptionsR :paid-options="safePaidOptions" />
			</div>
		</div>

		<!-- //////////////// -->
		<!-- INSTANCE DETAIL SETTINGS -->
		<!-- Настройки показываем только для WhatsApp-инстансов. -->
		<div class="idv__settings" v-if="isShowSettings">
			<InstanceDetailSettings />
		</div>
	</div>

	<!-- //////////////// -->
	<!-- Модалка. Получить QR код -->
	<ModalQrCode v-if="modalQrCode.isShow" />

	<!-- //////////////// -->
	<!-- Модалка. Лог действий -->
	<ModalEventLog v-if="modalEventLog.isShow" />
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import HeaderMobile from "@/components/header/HeaderMobile.vue";
import HeaderDesktop from "@/components/header/HeaderDesktop.vue";
import InstanceDetailInfo from "@/components/detail/InstanceDetailInfo.vue";
import InstanceDetailSettings from "@/components/detail/InstanceDetailSettings.vue";
import { mapActions, mapGetters } from "vuex";
import ModalQrCode from "@/components/modal/ModalQrCode.vue";
import { IModal } from "@/interfaces/IModal";
import ModalEventLog from "@/components/modal/ModalEventLog.vue";
import InstanceDetailOptions from "@/components/detail/InstanceDetailOptions.vue";
import { IPaidOptions } from "@/interfaces/IPaidOptions";
import { IOptions } from "@/interfaces/IOptions";
import { IInstanceDetail } from "@/interfaces/IInstanceDetail";
import InstanceDetailOptionsR from "@/components/detail/InstanceDetailOptionsR.vue";
import ErrorNoAccess from "@/components/errors/ErrorNoAccess.vue";
import ErrorInstanceLoad from "@/components/errors/ErrorInstanceLoad.vue";
import type { InstanceLoadStatus } from "@/store/instance/types";

/**
 * Отображает карточку инстанса выбранного канала и отдельно показывает
 * подтвержденный отказ в доступе либо ошибку получения данных из API.
 */
@Options({
	components: {
		ErrorNoAccess,
		ErrorInstanceLoad,
		InstanceDetailOptionsR,
		InstanceDetailOptions,
		ModalEventLog,
		ModalQrCode,
		InstanceDetailSettings,
		InstanceDetailInfo,
		HeaderDesktop,
		HeaderMobile,
	},

	computed: {
		...mapGetters("instanceModule", {
			modalQrCode: "modalQrCodeGetters",
			modalEventLog: "modalEventLogGetters",
			optionList: "optionListGetters",
			instance: "getInstance",
			instanceLoadStatus: "getInstanceLoadStatus",
		}),

		...mapGetters("paymentModule", {
			payOptionsLnkInstance: "payOptionsLnkInstanceGetters",
		}),
	},
	methods: {
		...mapActions("instanceModule", ["instanceAction", "optionListAction"]),
		...mapActions("paymentModule", ["paidOptionsAction"]),
	},
})
export default class InstanceDetailView extends Vue {
	// Константы
	public STATUS = {
		DELETE: 10,
		ACTIVE: 1,
		WAITING: 2,
		WAITING_5: 5,
		PROCESS: 0,
	};

	// ************************************************************
	// Define mapGetters
	modalQrCode: IModal;
	modalEventLog: IModal;
	payOptionsLnkInstance: Array<IPaidOptions> | null;
	public optionList: Array<IOptions> | null;
	public instance: IInstanceDetail | null;
	public instanceLoadStatus: InstanceLoadStatus;

	// ************************************************************
	// Define mapActions
	public instanceAction!: (payload: {
		instanceId: number;
		channel?: string;
	}) => Promise<void>;
	public paidOptionsAction!: (payload: { instanceId: number }) => Promise<void>;
	public optionListAction!: () => Promise<void>;

	/**
	 * Разрешает показ карточки после успешной загрузки и нормализации ID в хранилище.
	 */
	public get hasInstanceAccess(): boolean {
		return this.instanceLoadStatus === "loaded" && !!this.instance;
	}

	/**
	 * Возвращает признак явного отказа API в доступе к карточке (HTTP 403).
	 */
	public get isNoAccess(): boolean {
		return this.instanceLoadStatus === "forbidden";
	}

	/**
	 * Возвращает признак ошибки загрузки, включая пустой или некорректный ответ API.
	 */
	public get isLoadError(): boolean {
		return this.instanceLoadStatus === "error";
	}

	/**
	 * Безопасная ссылка на инстанс для шаблона после проверки доступа.
	 */
	public get safeInstance(): IInstanceDetail {
		return this.instance as IInstanceDetail;
	}

	/**
	 * Купленные опции всегда приводим к массиву, чтобы шаблон не падал на .length при null из API.
	 */
	public get safePaidOptions(): Array<IPaidOptions> {
		return Array.isArray(this.payOptionsLnkInstance)
			? this.payOptionsLnkInstance
			: [];
	}

	/**
	 * Список доступных опций всегда приводим к массиву, чтобы сравнение .length было безопасным.
	 */
	public get safeOptionList(): Array<IOptions> {
		return Array.isArray(this.optionList) ? this.optionList : [];
	}

	/**
	 * Показываем настройки только для WhatsApp-канала и не показываем для удаленных инстансов.
	 */
	public get isShowSettings(): boolean {
		const channel = this.currentChannel.toLowerCase();

		return (
			this.hasInstanceAccess &&
			this.safeInstance.status !== this.STATUS.DELETE &&
			["wapi", "whatsapp"].includes(channel)
		);
	}

	/**
	 * Берем канал из query, а если его нет - из данных инстанса.
	 */
	public get currentChannel(): string {
		return (
			(this.$route.query.channel as string) || this.instance?.channel || ""
		);
	}

	/**
	 * Загружает карточку по ID и каналу из маршрута, затем запрашивает опции
	 * только при успешном получении инстанса. Результат вызова не возвращается.
	 */
	public async mounted() {
		const instanceId = Number(this.$route.params.id);
		const channel = this.$route.query.channel as string;

		await this.instanceAction({ instanceId: instanceId, channel });

		if (this.hasInstanceAccess) {
			await this.paidOptionsAction({ instanceId: instanceId });
			await this.optionListAction();
		}
	}
}
</script>

<style scoped lang="scss">
.idv {
	&__header {
		background: rgb(37, 211, 102);
	}

	&__options {
		padding: 12px;
	}

	&__settings {
		padding-bottom: 24px;
	}
}
</style>
